import React, { useCallback, useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useHistory, useParams } from 'react-router';
import { EMPTY, race, timer } from 'rxjs';
import {
  catchError,
  concatMap,
  filter,
  switchMap,
  takeWhile,
  tap,
} from 'rxjs/operators';
import moment from 'moment';
import {
  REPORTING_VAAD_LIZ_REPORTS_CREATE,
  REPORTING_VAAD_LIZ_REPORTS_UPDATE,
  REPORTING_VAAD_LIZ_REPORTS_VIEW,
} from '../../../router/routes';
import { route } from '../../../services/utils';
import { useService } from '../../../services/services';
import { ReportListItemStatus } from './enums/report-list-item-status';
import { ReportListItem } from './interfaces/report-list-item';
import DashboardLoading from '../../dashboard/components/dashboard-loading';
import Spinner from '../../../components/common/spinner';
import StaticTable, {
  StaticTableColumn,
} from '../../../components/common/table/static-table';
import {
  ButtonComponent,
  ButtonStyling,
} from '../../../components/form/elements/button';
import ExcelButton from '../../../components/common/table/excel-button/excel-button';
import LoadingModal from '../../../components/modals/loading-modal';
import { useErrorModal } from './components/use-error-modal';
import SubmitConfirmationModal from './components/submit-confirmation-modal';
import TutorialModal from './components/tutorial-modal';
import RemoveBtn from './components/remove-btn';
import RemoveLizBtn from './components/remove-liz-btn';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus } from '@fortawesome/pro-regular-svg-icons';
import css from './index.module.css';

interface Props {
  year: string;
  data: ReportListItem[];
}

interface ReportListData {
  rows: ReportListItem[];
}

const POLL_INTERVAL = 3000;
const TIMEOUT_INTERVAL = 30 * 60 * 1000;
const NOVEMBER_5_DEADLINE = moment('2024-11-06T00:00:00.000000Z');

export default function ReportList({ year, data }: Props) {
  const history = useHistory();
  const { t } = useTranslation();
  const transKeyPrefix = 'pages.crop.vaad_liz_reports_v2.';
  const tp = useCallback((key: string) => t(transKeyPrefix.concat(key)), [t]);
  const { farmId } = useParams<{ farmId: string }>();
  const { vaadLizReportV2Service: vaadLizReportService } = useService();
  const [isLoading, setIsLoading] = useState(false);
  const [tableData, setTableData] = useState<ReportListData | null>(null);
  const { errorModal, showError } = useErrorModal();
  const [loadingModal, setLoadingModal] = useState(null);
  const [submitConfirmationModal, setSubmitConfirmationModal] = useState(null);
  const [tutorialModal, setTutorialModal] = useState(null);
  // const [currentDate, setCurrentDate] = useState<string | null>(null);

  const onDataError = useCallback(
    (error) => {
      console.error('Error fetching data', error);
      showError(error);
      setIsLoading(false);
      setLoadingModal(null);
    },
    [showError]
  );

  const onDataLoad = useCallback(
    (data: ReportListItem[]) => {
      const send = (id: string) => {
        setLoadingModal(
          <LoadingModal
            content={
              <Trans
                i18nKey={`${transKeyPrefix}loading_modal.send_to_liz`}
                components={{ 1: <br /> }}
              />
            }
            onClose={() => setLoadingModal(null)}
          />
        );
        vaadLizReportService
          .send(farmId, year, id)
          .subscribe(() => validateLiz(id), onDataError);
      };

      const validateLiz = (id: string) => {
        setLoadingModal(
          <LoadingModal
            content={
              <Trans
                i18nKey={`${transKeyPrefix}loading_modal.validate_liz`}
                components={{ 1: <br /> }}
              />
            }
            onClose={() => setLoadingModal(null)}
          />
        );

        vaadLizReportService
          .validateLiz(farmId, id)
          .pipe(
            switchMap(() => {
              const timeout$ = timer(TIMEOUT_INTERVAL).pipe(
                tap(() => {
                  showError(
                    new Error(
                      t(`${transKeyPrefix}list.modal.error.validation_timeout`)
                    )
                  );
                  setIsLoading(true);
                })
              );

              const polling$ = timer(0, POLL_INTERVAL).pipe(
                switchMap(() =>
                  vaadLizReportService.validateLizStatus(farmId, id)
                ),
                takeWhile(
                  (statusResponse) =>
                    statusResponse.liz_validation_status !== 'finished' &&
                    statusResponse.liz_validation_status !== 'failed',
                  true
                ),
                tap((statusResponse) => {
                  if (statusResponse.liz_validation_status === 'failed') {
                    setIsLoading(true);
                    showError(
                      new Error(
                        t(`${transKeyPrefix}list.modal.error.validation_failed`)
                      )
                    );
                  }
                }),
                filter(
                  (statusResponse) =>
                    statusResponse.liz_validation_status === 'finished' ||
                    statusResponse.liz_validation_status === 'failed'
                )
              );

              return race(timeout$, polling$).pipe(
                catchError((error) => {
                  onDataError(error);
                  return EMPTY;
                })
              );
            }),
            concatMap(() => vaadLizReportService.index(farmId)),
            tap((data) => onDataLoad(data)),
            catchError((error) => {
              onDataError(error);
              return EMPTY;
            })
          )
          .subscribe();
      };

      const remove = (id: string) => {
        setLoadingModal(
          <LoadingModal
            content={
              <Trans
                i18nKey={`${transKeyPrefix}loading_modal.remove`}
                components={{ 1: <br /> }}
              />
            }
            onClose={() => setLoadingModal(null)}
          />
        );
        return vaadLizReportService.remove(farmId, id).pipe(
          concatMap(() => vaadLizReportService.index(farmId)), // Fetch updated data after removal
          tap((data) => onDataLoad(data)),
          catchError((error) => {
            onDataError(error);
            return EMPTY;
          })
        );
      };

      const removeLiz = (id: string) => {
        setLoadingModal(
          <LoadingModal
            content={
              <Trans
                i18nKey={`${transKeyPrefix}loading_modal.remove_liz`}
                components={{ 1: <br /> }}
              />
            }
            onClose={() => setLoadingModal(null)}
          />
        );
        return vaadLizReportService.removeLiz(farmId, id).pipe(
          concatMap(() => vaadLizReportService.index(farmId)), // Fetch updated data after removal
          tap((data) => onDataLoad(data)),
          catchError((error) => {
            onDataError(error);
            return EMPTY;
          })
        );
      };

      const view = (row: ReportListItem) => {
        const routeTo = route(REPORTING_VAAD_LIZ_REPORTS_VIEW, {
          farmId,
          year,
          id: row.id,
          reportTab: 'lad-fields',
        });

        history.push(routeTo);
      };

      const edit = (row: ReportListItem) => {
        const routeTo = route(REPORTING_VAAD_LIZ_REPORTS_UPDATE, {
          farmId,
          year,
          id: row.id,
          reportTab: 'lad-fields',
        });

        history.push(routeTo);
      };

      const submitLiz = (id: string) => {
        setLoadingModal(
          <LoadingModal
            content={
              <Trans
                i18nKey={`${transKeyPrefix}loading_modal.submit_liz`}
                components={{ 1: <br /> }}
              />
            }
            onClose={() => setLoadingModal(null)}
          />
        );

        vaadLizReportService
          .submitLiz(farmId, id)
          .pipe(
            switchMap(() => {
              const timeout$ = timer(TIMEOUT_INTERVAL).pipe(
                tap(() => {
                  showError(
                    new Error(
                      t(`${transKeyPrefix}list.modal.error.validation_timeout`)
                    )
                  );
                  setIsLoading(true);
                })
              );

              const polling$ = timer(0, POLL_INTERVAL).pipe(
                switchMap(() =>
                  vaadLizReportService.validateLizStatus(farmId, id)
                ),
                takeWhile(
                  (statusResponse) =>
                    statusResponse.liz_validation_status !== 'finished' &&
                    statusResponse.liz_validation_status !== 'failed',
                  true
                ),
                tap((statusResponse) => {
                  if (statusResponse.liz_validation_status === 'failed') {
                    setIsLoading(true);
                    showError(
                      new Error(
                        t(`${transKeyPrefix}list.modal.error.validation_failed`)
                      )
                    );
                  }
                }),
                filter(
                  (statusResponse) =>
                    statusResponse.liz_validation_status === 'finished' ||
                    statusResponse.liz_validation_status === 'failed'
                )
              );

              return race(timeout$, polling$).pipe(
                catchError((error) => {
                  onDataError(error);
                  return EMPTY;
                })
              );
            }),
            concatMap(() => vaadLizReportService.index(farmId)),
            tap((data) => onDataLoad(data)),
            catchError((error) => {
              onDataError(error);
              return EMPTY;
            })
          )
          .subscribe();
      };

      const smartSubmitLiz = (id: string, status: ReportListItemStatus) => {
        if (status === ReportListItemStatus.VALIDATION_FAILED) {
          setSubmitConfirmationModal(
            <SubmitConfirmationModal
              onConfirm={() => {
                submitLiz(id);
                setSubmitConfirmationModal(null);
              }}
              onCancel={() => {
                setSubmitConfirmationModal(null);
              }}
            />
          );
        } else {
          submitLiz(id);
        }
      };

      const hasSubmittedBeforeNovember5 = data.some(
        (row) =>
          row.status !== ReportListItemStatus.CREATED &&
          row.status !== ReportListItemStatus.EDITED &&
          row.sent_to_liz_at &&
          moment(row.sent_to_liz_at).isBefore(moment(NOVEMBER_5_DEADLINE))
      );

      data?.forEach((row: ReportListItem) => {
        const shouldShowDateInStatus =
          row.status === ReportListItemStatus.EDITED ||
          row.status === ReportListItemStatus.SUBMITTED_VALIDATION_FALSE ||
          row.status === ReportListItemStatus.SUBMITTED_VALIDATION_TRUE;

        const shouldShowRemoveButton =
          row.status === ReportListItemStatus.CREATED ||
          row.status === ReportListItemStatus.EDITED ||
          (row.status === ReportListItemStatus.VALIDATION_FAILED &&
            !row.sent_to_liz_at);

        const shouldShowRemoveLizButton = false;

        const shouldShowValidateButton =
          row.status === ReportListItemStatus.READY_FOR_VALIDATION ||
          row.status === ReportListItemStatus.SENT;

        const shouldShowSendButton =
          !hasSubmittedBeforeNovember5 &&
          (row.status === ReportListItemStatus.CREATED ||
            row.status === ReportListItemStatus.EDITED ||
            (row.status === ReportListItemStatus.VALIDATION_FAILED &&
              !row.sent_to_liz_at));

        const shouldShowEditButton =
          !hasSubmittedBeforeNovember5 &&
          (row.status === ReportListItemStatus.CREATED ||
            row.status === ReportListItemStatus.EDITED);

        const shouldShowSubmitButton =
          !hasSubmittedBeforeNovember5 &&
          (row.status === ReportListItemStatus.VALIDATION_SUCCEEDED ||
            (row.status === ReportListItemStatus.VALIDATION_FAILED &&
              row.sent_to_liz_at));

        const shouldShowViewButton =
          hasSubmittedBeforeNovember5 ||
          (row.status !== ReportListItemStatus.CREATED &&
            row.status !== ReportListItemStatus.EDITED);

        row.actions = (
          <>
            {shouldShowEditButton ? (
              <ButtonComponent
                name={'edit-btn-' + row.id}
                label={tp('list.edit')}
                click={() => edit(row)}
                styling={ButtonStyling.primary}
              />
            ) : null}

            {shouldShowViewButton ? (
              <ButtonComponent
                name={'view-btn-' + row.id}
                label={tp('list.view')}
                click={() => view(row)}
                styling={ButtonStyling.cancel}
              />
            ) : null}

            {shouldShowRemoveButton ? (
              <RemoveBtn id={row.id} onRemove={remove}></RemoveBtn>
            ) : null}
          </>
        );

        row.actionsLiz = (
          <>
            {shouldShowSendButton ? (
              <ButtonComponent
                name={'send-btn-' + row.id}
                label={tp('list.send')}
                click={() => send(row.id)}
              />
            ) : null}

            {shouldShowValidateButton ? (
              <ButtonComponent
                name={'validate-liz-btn-' + row.id}
                label={tp('list.validate_liz')}
                click={() => validateLiz(row.id)}
              />
            ) : null}

            {shouldShowSubmitButton ? (
              <ButtonComponent
                name={'submit-liz-btn-' + row.id}
                label={tp('list.submit_liz')}
                click={() => smartSubmitLiz(row.id, row.status)}
              />
            ) : null}

            {shouldShowRemoveLizButton ? (
              <RemoveLizBtn id={row.id} onRemove={removeLiz}></RemoveLizBtn>
            ) : null}
          </>
        );

        let statusText = tp('list.status.' + row.status);
        if (shouldShowDateInStatus) {
          statusText += ' ' + moment(row.updated_at).format('DD.MM.YYYY');
        }
        row.status_value = (
          <span className={css[row.status]}>
            {statusText}
            {(row.status === ReportListItemStatus.VALIDATION_FAILED ||
              row.status ===
                ReportListItemStatus.SUBMITTED_VALIDATION_FALSE) && (
              <ExcelButton
                onClick={() => {
                  return vaadLizReportService
                    .exportListXls(farmId, row.id)
                    .subscribe();
                }}
              />
            )}
          </span>
        );
        row.created_at_value = moment(row.created_at).format(
          'DD.MM.YYYY | HH:mm'
        );
      });
      setTableData({ rows: data });
      setIsLoading(false);
      setLoadingModal(null);
    },
    [farmId, history, onDataError, showError, t, tp, vaadLizReportService, year]
  );

  // useEffect(() => {
  //   vaadLizReportService.getCurrentDate(farmId).subscribe(
  //     (date) => {
  //       setCurrentDate(date);
  //       setIsLoading(false);
  //     },
  //     (error) => onDataError(error)
  //   );
  // }, [vaadLizReportService, onDataError, farmId]);

  useEffect(() => {
    // if (currentDate) {
    setIsLoading(true);
    onDataLoad(data);
    // }
  }, [data, onDataLoad]);
  // }, [currentDate, data, onDataLoad]);

  // todo: cleanup or remove
  // useEffect(() => {
  //   const subscription = subscriptionRef.current;
  //
  //   return () => {
  //     if (subscription) {
  //       subscription.unsubscribe();
  //     }
  //   }
  // }, []);

  const onAdd = useCallback(() => {
    setIsLoading(true);
    vaadLizReportService.add(farmId).subscribe(({ id }) => {
      setIsLoading(false);
      const routeTo = route(REPORTING_VAAD_LIZ_REPORTS_CREATE, {
        farmId,
        year,
        id,
        reportTab: 'lad-fields',
      });

      history.push(routeTo);
    }, onDataError);
  }, [farmId, history, onDataError, vaadLizReportService, year]);

  const onSmartAdd = useCallback(() => {
    setTutorialModal(
      <TutorialModal
        onConfirm={() => {
          setTutorialModal(null);
          onAdd();
        }}
        onCancel={() => setTutorialModal(null)}
      />
    );
  }, [onAdd]);

  const columns: StaticTableColumn<ReportListItem>[] = [
    {
      accessor: 'created_at_value',
      header: tp('list.created'),
      className: `${css['column-wide']} ${css['strong']}`,
    },
    {
      accessor: 'status_value',
      header: tp('list.status.title'),
      className: css['column-wide'],
    },
    { accessor: 'actions', header: tp('list.actions') },
    { accessor: 'actionsLiz', header: tp('list.actions_liz') },
  ];

  // return (isLoading || !currentDate) ? (
  return isLoading ? (
    <>
      <DashboardLoading />

      {errorModal}
    </>
  ) : (
    <div>
      <div className="flex justify-between mb-4">
        <div>
          <button
            data-name={`collect-data-button-create`}
            disabled={isLoading}
            onClick={onSmartAdd}
            className={`${css['create-button']}`}
          >
            <FontAwesomeIcon icon={faPlus} />
            <span className={css['button-text']}>{tp('create_button')}</span>
            <Spinner visible={isLoading} size="sm" />
          </button>
        </div>

        <div className={css['list-warning']}>
          <Trans
            i18nKey="pages.crop.vaad_liz_reports_v2.list.warning"
            components={{ 1: <br /> }}
          />
        </div>
      </div>

      <div className={css['body']}>
        {tableData && (
          <div className={`${css['table']} ${css['list-table']}`}>
            <StaticTable<ReportListItem>
              data={tableData}
              keyAccessor="id"
              columns={columns}
              fixedHeight
            />
          </div>
        )}
      </div>

      {submitConfirmationModal}
      {tutorialModal}
      {errorModal}
      {loadingModal}
    </div>
  );
}
